/**
 * Route chunk prefetching.
 *
 * The app is code-split per route, so the first click on any nav item pays a
 * network round-trip for that chunk. We warm the chunks a visitor is most
 * likely to open next while the browser is idle, and on link hover/touch —
 * which makes in-app navigation feel instant instead of showing a loader.
 *
 * Prefetch is best-effort: failures are swallowed, and nothing here blocks
 * rendering or route resolution.
 */
import { runAfterIdle, runAfterLcp } from "@/lib/afterIdle";

type Loader = () => Promise<unknown>;

/** Chunks worth warming for a signed-out visitor landing on a public page. */
const PUBLIC_ROUTES: Record<string, Loader> = {
  "/quote": () => import("@/pages/Quote"),
  "/results": () => import("@/pages/Results"),
  "/login": () => import("@/pages/Login"),
  "/how-it-works": () => import("@/pages/HowItWorks"),
};

/** Chunks worth warming once we know someone is signed in. */
const AUTHED_ROUTES: Record<string, Loader> = {
  "/account": () => import("@/account/pages/AccountDashboard"),
  "/courier": () => import("@/courier/pages/Dashboard"),
};

const done = new Set<string>();
const saveData = () => {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !!c?.saveData || /2g/.test(c?.effectiveType ?? "");
};

function warm(key: string, loader: Loader) {
  if (done.has(key) || saveData()) return;
  done.add(key);
  void loader().catch(() => done.delete(key));
}

/**
 * Warm the highest-value chunks once the current page has finished painting
 * its largest contentful element and the browser is idle — never before, so
 * prefetching cannot queue ahead of the fonts/content the visitor is waiting for.
 */
export function prefetchLikelyRoutes(opts: { authed: boolean }) {
  let inner: { cancel: () => void } | null = null;
  const outer = runAfterLcp(() => {
    inner = runAfterIdle(() => {
      Object.entries(PUBLIC_ROUTES).forEach(([k, l]) => warm(k, l));
      if (opts.authed) Object.entries(AUTHED_ROUTES).forEach(([k, l]) => warm(k, l));
    }, 3000);
  });
  return { cancel: () => { outer.cancel(); inner?.cancel(); } };
}

/** Warm a single route chunk on hover / touchstart of a link. */
export function prefetchRoute(path: string) {
  const clean = path.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  const loader = PUBLIC_ROUTES[clean] ?? AUTHED_ROUTES[clean];
  if (loader) warm(clean, loader);
}
